import FW from '../fw/fw';
import Env from '../fw/env';
import Logger from '../fw/logger';
import Transcripts from '../accessibility/transcripts';
import Thumbnails from '../accessibility/thumbnails';
import QuickSeekManagement from './quick';


export default class SeekManagement {

  #rmp;
  #draggingTimeCursor = false;
  #touchDown = false;
  #onMouseupSeekFn = null;
  #onMousemoveUpdateCurrentTimeFn = null;
  #hideIndicatorTimeout = null;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  get draggingTimeCursor() {
    return this.#draggingTimeCursor;
  }

  get touchDown() {
    return this.#touchDown;
  }

  #calculateTimeFromEventInPercent(event) {
    let posX;
    if (event.type === 'touchmove' || event.type === 'touchend') {
      posX = FW.getPosXTouch(event);
    } else {
      posX = FW.getPosX(event);
    }
    const timeTotalX = FW.getAbsLeft(this.#rmp.dom.timetotal);
    const widthTimeTotal = FW.getPrecisionWidthWithPaddingAndBorder(this.#rmp.dom.timetotal);
    let time;
    if (posX < timeTotalX) {
      time = 0;
    } else if (posX > (timeTotalX + widthTimeTotal)) {
      time = 1;
    } else {
      time = (posX - timeTotalX) / widthTimeTotal;
    }
    return time;
  }

  #updateCurrentTime(event) {
    if (event) {
      let value = this.#calculateTimeFromEventInPercent(event) * 100;
      let sanitizedValue;
      if (value >= 100) {
        sanitizedValue = 100;
      } else if (value < 0) {
        sanitizedValue = 0;
      } else {
        sanitizedValue = value;
      }
      const duration = this.#rmp.duration;
      if (duration > -1) {
        this.#rmp.dom.current.style.width = sanitizedValue + '%';
        this.#rmp.dom.handle.style.left = sanitizedValue + '%';
        let textValue, element;
        if (this.#rmp.isDvr && this.#rmp.dom.durationText) {
          this.#rmp.dom.container.classList.replace('rmp-dvr-live', 'rmp-dvr-vod');
          element = this.#rmp.dom.durationText;
          textValue = '-' + FW.readableTime((duration - ((sanitizedValue / 100) * duration)) / 1000);
        } else if (this.#rmp.dom.timeElapsedText) {
          element = this.#rmp.dom.timeElapsedText;
          textValue = FW.readableTime((sanitizedValue * (duration / 1000)) / 100);
          this.#rmp.rmpUtils.updateDurationText(duration, (sanitizedValue * duration) / 100);
        }
        FW.setText(element, textValue);
      }
    }
  }

  #onTouchendSeek(event) {
    if (event) {
      try {
        event.stopPropagation();
        event.preventDefault();
      } catch (error) {
        this.#rmp.rmpUtils.warning(1050, error);
      }
      const time = this.#calculateTimeFromEventInPercent(event);
      this.#updateCurrentTime(event);
      this.executeSeek(time);
      this.#touchDown = false;
    }
  }

  #onTouchmoveUpdateCurrentTime(event) {
    this.#touchDown = true;
    this.#rmp.rmpUIManagement.showChrome();
    this.#updateCurrentTime(event);
  }

  #onMouseupSeek(event) {
    if (event) {
      event.stopPropagation();
      this.#draggingTimeCursor = false;
      this.#rmp.dom.timetotal.classList.remove('grabbing');
      document.removeEventListener('mouseup', this.#onMouseupSeekFn);
      document.removeEventListener('mousemove', this.#onMousemoveUpdateCurrentTimeFn);
      const time = this.#calculateTimeFromEventInPercent(event);
      this.#updateCurrentTime(event);
      this.executeSeek(time);
    }
  }

  #getThumbnailImageTrack() {
    const imageTracks = this.#rmp.shakaPlayer.getImageTracks();
    if (imageTracks && imageTracks.length > 0) {
      const mimeTypesPreference = [
        'image/jpeg',
        'image/png',
        'image/svg+xml',
      ];
      for (const mimeType of mimeTypesPreference) {
        const estimatedBandwidth = this.#rmp.shakaPlayer.getStats().estimatedBandwidth;
        const bestOptions = imageTracks.filter(track => {
          return track.mimeType.toLowerCase() === mimeType && track.bandwidth < estimatedBandwidth * 0.01;
        }).sort((a, b) => {
          return b.bandwidth - a.bandwidth;
        });
        if (bestOptions && bestOptions.length > 0) {
          return bestOptions[0];
        }
      }
      return imageTracks[0];
    }
    return null;
  }

  async #showThumbnail(imageTrack, currentTimeHover) {
    let thumbnail;
    try {
      thumbnail = await this.#rmp.shakaPlayer.getThumbnails(imageTrack.id, currentTimeHover);
    } catch (error) {
      this.#rmp.rmpUtils.warning(3008, error);
    }
    if (!thumbnail) {
      return;
    }
    const uri = thumbnail.uris[0];
    this.#rmp.dom.thumbnailsElement.style.backgroundImage = 'url(' + uri + ')';
    this.#rmp.dom.thumbnailsElement.style.backgroundPositionX = '-' + thumbnail.positionX + 'px';
    this.#rmp.dom.thumbnailsElement.style.backgroundPositionY = '-' + thumbnail.positionY + 'px';
    this.#rmp.dom.thumbnailsElement.style.height = thumbnail.height + 'px';
    this.#rmp.dom.thumbnailsElement.style.width = thumbnail.width + 'px';
    this.#rmp.dom.thumbnailsElement.style.top = '-' + thumbnail.height + 'px';
    this.#rmp.dom.indicator.style.width = thumbnail.width + 'px';
    this.#rmp.dom.indicator.style.marginLeft = -(thumbnail.width / 2) + 'px';
  }

  #mouseMoveIndicator(e) {
    if (e.type === 'touchmove') {
      FW.show(this.#rmp.dom.indicator);
    }
    let duration = this.#rmp.duration;
    if (duration <= 0) {
      return;
    }
    duration = duration / 1000;

    let posX;
    if (e.type === 'touchmove') {
      posX = FW.getPosXTouch(e);
    } else {
      posX = FW.getPosX(e);
    }

    const containerLeft = FW.getAbsLeft(this.#rmp.dom.container);
    const seekBarLeft = FW.getAbsLeft(this.#rmp.dom.seekBar);
    const seekBarWidth = FW.getPrecisionWidthWithPaddingAndBorder(this.#rmp.dom.seekBar);
    const timeIndicatorWidth = FW.getPrecisionWidthWithPaddingAndBorder(this.#rmp.dom.indicator);

    let posHover = posX - containerLeft;
    const min = seekBarLeft - containerLeft + (timeIndicatorWidth / 2);
    const max = seekBarLeft - containerLeft + seekBarWidth - (timeIndicatorWidth / 2);
    if (posHover < min) {
      posHover = min;
    } else if (posHover > max) {
      posHover = max;
    }

    const posHoverPercent = this.#calculateTimeFromEventInPercent(e);
    let currentTimeHover;
    if (this.#rmp.isDvr) {
      currentTimeHover = duration - (posHoverPercent * duration);
    } else {
      currentTimeHover = posHoverPercent * duration;
    }
    if (currentTimeHover >= duration) {
      currentTimeHover = duration;
    } else if (currentTimeHover <= 0) {
      currentTimeHover = 0;
    }
    let textValue;
    if (this.#rmp.isDvr) {
      if (currentTimeHover === 0) {
        textValue = this.#rmp.labels.hint.live;
      } else {
        textValue = '-' + FW.readableTime(currentTimeHover);
      }
    } else {
      textValue = FW.readableTime(currentTimeHover);
    }
    FW.setText(this.#rmp.dom.timeIndicator, textValue);
    this.#rmp.dom.indicator.style.left = posHover + 'px';

    const containerWidth = FW.getPrecisionWidthWithPaddingAndBorder(this.#rmp.dom.container);
    const rmpThumbnails = this.#rmp.rmpThumbnails;
    if (rmpThumbnails && rmpThumbnails.array.length > 0) {
      rmpThumbnails.array.forEach(thumbnail => {
        if (thumbnail[0] !== null && thumbnail[1] !== null &&
          typeof thumbnail[2] === 'string' && currentTimeHover >= thumbnail[0] &&
          currentTimeHover < thumbnail[1]) {
          if (rmpThumbnails.uris.length > 1) {
            let thumbnailsURL;
            rmpThumbnails.urisTimestamps.forEach((urisTimestamps, index) => {
              if (currentTimeHover >= urisTimestamps) {
                thumbnailsURL = rmpThumbnails.uris[index];
              }
            });
            if (thumbnailsURL && thumbnailsURL !== rmpThumbnails.currentURI) {
              Logger.print('thumbnails', 'changing sprite source to ' + thumbnailsURL);
              rmpThumbnails.currentURI = thumbnailsURL;
              this.#rmp.dom.thumbnailsElement.style.backgroundImage = 'url("' + thumbnailsURL + '")';
            }
          }
          const split = (thumbnail[2]).split(',');
          const x = (split[0]).trim();
          const y = (split[1]).trim();
          const width = (split[2]).trim();
          const height = (split[3]).trim();
          this.#rmp.dom.thumbnailsElement.style.backgroundPosition = '-' + x + 'px ' + '-' + y + 'px';
          this.#rmp.dom.thumbnailsElement.style.height = height + 'px';
          this.#rmp.dom.thumbnailsElement.style.width = width + 'px';
          this.#rmp.dom.thumbnailsElement.style.top = '-' + height + 'px';
          this.#rmp.dom.indicator.style.width = width + 'px';
          this.#rmp.dom.indicator.style.marginLeft = -(width / 2) + 'px';
          const imgLeft = FW.getAbsLeft(this.#rmp.dom.thumbnailsElement);
          if (this.#rmp.skin === 's2') {
            if (imgLeft < containerLeft) {
              this.#rmp.dom.indicator.style.left = '0';
              this.#rmp.dom.indicator.style.marginLeft = '0';
            } else if (imgLeft + parseFloat(width) > containerLeft + containerWidth) {
              this.#rmp.dom.indicator.style.left = (containerWidth - 16 - parseFloat(width) / 2) + 'px';
            }
          }
        }
      });
    } else if (this.#rmp.rmpShakaPlayer && this.#rmp.rmpShakaPlayer.hasThumbnailTrack) {
      if (!this.#rmp.dom.thumbnailsElement) {
        if (!rmpThumbnails) {
          this.#rmp.rmpThumbnails = new Thumbnails(this.#rmp);
        }
        this.#rmp.rmpThumbnails.appendThumbnailsElement();
      }
      const imageTrack = this.#getThumbnailImageTrack();
      if (imageTrack) {
        this.#showThumbnail(imageTrack, currentTimeHover);
      }
    }
  }

  #mouseEnterIndicator() {
    if (!this.#rmp.hasLoadedMetadata) {
      return;
    }
    window.clearTimeout(this.#hideIndicatorTimeout);
    FW.show(this.#rmp.dom.indicator);
  }

  #mouseLeaveIndicator() {
    if (!this.#rmp.hasLoadedMetadata) {
      return;
    }
    window.clearTimeout(this.#hideIndicatorTimeout);
    this.#hideIndicatorTimeout = window.setTimeout(() => {
      FW.hide(this.#rmp.dom.indicator);
    }, 400);
  }

  #initIndicator() {
    const _mouseMoveIndicatorFn = this.#mouseMoveIndicator.bind(this);
    const _mouseLeaveIndicatorFn = this.#mouseLeaveIndicator.bind(this);
    const _mouseEnterIndicatorFn = this.#mouseEnterIndicator.bind(this);
    this.#rmp.dom.timetotal.addEventListener('mousemove', _mouseMoveIndicatorFn);
    this.#rmp.dom.timetotal.addEventListener('touchmove', _mouseMoveIndicatorFn,
      Env.hasPassiveEventListeners ? { passive: true } : false);
    this.#rmp.dom.timetotal.addEventListener('mouseenter', _mouseEnterIndicatorFn);
    FW.addEvents(['touchend', 'mouseleave'], this.#rmp.dom.timetotal, _mouseLeaveIndicatorFn);
  }

  #setDvrVodUI() {
    this.#rmp.isDvrLiveMode = false;
    this.#rmp.dom.container.classList.replace('rmp-dvr-live', 'rmp-dvr-vod');
    FW.setText(this.#rmp.dom.durationText, '');
    this.#rmp.dispatch('dvrisvod');
  }

  destroy() {
    window.clearTimeout(this.#hideIndicatorTimeout);
    document.removeEventListener('mouseup', this.#onMouseupSeekFn);
    document.removeEventListener('mousemove', this.#onMousemoveUpdateCurrentTimeFn);
  }

  appendSeekBar() {
    const rtl = this.#rmp.rtl;
    // seekBar
    if (typeof this.#rmp.dom.seekBar !== 'undefined') {
      return;
    }
    this.#rmp.dom.seekBar = FW.createElement('div', 'rmp-seek-bar');
    if (this.#rmp.hideSeekBar) {
      this.#rmp.dom.seekBar.classList.add('rmp-no-display');
    }
    this.#rmp.dom.timetotal = FW.createElement('div', 'rmp-seek-bar-sliding-area');
    // loaded/current/handle only when VOD or DVR
    this.#rmp.dom.loaded = FW.createElement('div', 'rmp-seek-bar-sliding-loaded');
    this.#rmp.dom.timetotal.appendChild(this.#rmp.dom.loaded);
    this.#rmp.dom.current = FW.createElement('div', 'rmp-seek-bar-sliding-current-position rmp-color-bg-sliding');
    this.#rmp.dom.timetotal.appendChild(this.#rmp.dom.current);
    this.#rmp.dom.handle = FW.createElement('div', 'rmp-seek-bar-sliding-handle rmp-color-bg-sliding');
    this.#rmp.dom.timetotal.appendChild(this.#rmp.dom.handle);
    this.#rmp.dom.seekBar.addEventListener('touchend', this.#onTouchendSeek.bind(this));
    this.#rmp.dom.seekBar.addEventListener('touchmove', this.#onTouchmoveUpdateCurrentTime.bind(this),
      Env.hasPassiveEventListeners ? { passive: true } : false);
    this.#onMouseupSeekFn = this.#onMouseupSeek.bind(this);
    this.#onMousemoveUpdateCurrentTimeFn = this.#updateCurrentTime.bind(this);
    this.#rmp.dom.timetotal.addEventListener('mousedown', event => {
      if (event) {
        event.stopPropagation();
      }
      this.#draggingTimeCursor = true;
      this.#rmp.dom.timetotal.classList.add('grabbing');
      document.addEventListener('mouseup', this.#onMouseupSeekFn);
      document.addEventListener('mousemove', this.#onMousemoveUpdateCurrentTimeFn);
    });

    // mobile or live === no indicator
    this.#rmp.dom.indicator = FW.createElement('div', 'rmp-indicator rmp-color-bg');
    this.#rmp.dom.timeIndicator = FW.createElement('span', 'rmp-time-indicator', null, rtl);
    this.#rmp.dom.indicator.appendChild(this.#rmp.dom.timeIndicator);
    this.#rmp.dom.controlBar.appendChild(this.#rmp.dom.indicator);
    this.#initIndicator();

    // quickRewind || quickForward
    if (this.#rmp.quickRewind > 0 || this.#rmp.quickForward > 0 || this.#rmp.tvPlayerSkin) {
      this.#rmp.rmpQuickSeekManagement = new QuickSeekManagement(this.#rmp);
      this.#rmp.rmpQuickSeekManagement.init();
    }

    // seekBarThumbnailsLoc
    if (this.#rmp.seekBarThumbnailsLoc !== '') {
      this.#rmp.rmpThumbnails = new Thumbnails(this.#rmp);
      this.#rmp.rmpThumbnails.init();
    }
    if (this.#rmp.rmpInitManagement.hasTranscripts) {
      const rmpTranscripts = new Transcripts(this.#rmp);
      rmpTranscripts.init();
    }
    this.#rmp.dom.seekBar.appendChild(this.#rmp.dom.timetotal);
    if (this.#rmp.dom.time.nextSibling) {
      FW.insertBefore(this.#rmp.dom.controlBar, this.#rmp.dom.seekBar, this.#rmp.dom.time.nextSibling);
    } else {
      this.#rmp.dom.controlBar.appendChild(this.#rmp.dom.seekBar);
    }
    this.#rmp.rmpAccessible.button(this.#rmp.dom.seekBar, this.#rmp.labels.hint.seek);

    // duration
    this.#rmp.dom.durationText = FW.createElement('span', 'rmp-duration-text', null, rtl);
    this.#rmp.dom.timeSeparator = FW.createElement('span', 'rmp-time-separator', '/', rtl);
    if (this.#rmp.audioOnly) {
      FW.showInline(this.#rmp.dom.timeSeparator);
    }
    this.#rmp.dom.time.appendChild(this.#rmp.dom.timeSeparator);
    this.#rmp.dom.time.appendChild(this.#rmp.dom.durationText);

    // if already available upate duration
    const duration = this.#rmp.duration;
    const currentTime = this.#rmp.currentTime;
    if (duration > -1 && currentTime > -1) {
      FW.showInline(this.#rmp.dom.timeSeparator);
      if (this.#rmp.isVod) {
        this.#rmp.rmpUtils.updateDurationText(duration, currentTime);
      }
    }

    //aria-valuenow === only for VOD and DVR
    if (duration > -1 && currentTime > -1) {
      const valuenow = (FW.precisionRound((currentTime * 100) / duration, 2)).toString();
      const valuetext = FW.readableTime(currentTime / 1000) + ' / ' + FW.readableTime(duration / 1000);
      this.#rmp.dom.seekBar.setAttribute('aria-valuenow', valuenow);
      this.#rmp.dom.seekBar.setAttribute('aria-valuetext', valuetext);
    }
  }

  executeSeek(time) {
    let duration;
    let seekToTime;
    let slide;
    if (this.#rmp.googleCastConnected) {
      const castDuration = this.#rmp.duration;
      if (castDuration === -1) {
        duration = -1;
      } else {
        duration = castDuration / 1000;
      }
      seekToTime = duration * time;
    } else {
      if (this.#rmp.isDvr && this.#rmp.rmpDvrManagement) {
        if (this.#rmp.isDvrLiveMode &&
          (this.#rmp.rmpDvrManagement.resyncPauseTime > 0 || this.#rmp.rmpDvrManagement.resyncDvrCurrentTime > 0)) {
          this.#rmp.rmpDvrManagement.unwireDvrEvents();
          this.#rmp.rmpDvrManagement.resyncPauseTime = 0;
          this.#rmp.rmpDvrManagement.resyncDvrCurrentTime = 0;
          this.#rmp.rmpDvrManagement.wirePauseDvrEvents();
        }
        if (this.#rmp.rmpHlsJS) {
          duration = FW.getMediaDuration(this.#rmp.dom.video);
          if (!FW.isFiniteNumber(duration)) {
            return;
          }
          slide = duration - this.#rmp.rmpHlsJS.hlsJSLevelDuration;
          if (slide <= 0) {
            slide = 0;
          }
          seekToTime = (this.#rmp.rmpHlsJS.hlsJSLevelDuration * time) + slide;
        } else if (this.#rmp.readingShaka) {
          const seekRange = this.#rmp.shakaPlayer.seekRange();
          const start = seekRange.start;
          const end = seekRange.end;
          if (FW.isFiniteNumber(end) && FW.isFiniteNumber(start) && start >= 0 && end > 0) {
            duration = end;
            seekToTime = start + ((end - start) * time);
          }
        }
      } else {
        duration = FW.getMediaDuration(this.#rmp.dom.video);
        seekToTime = duration * time;
      }
    }
    if (!FW.isFiniteNumber(duration) || duration <= 0 || !FW.isFiniteNumber(time)) {
      return;
    }
    if (seekToTime < 0 || seekToTime > duration) {
      return;
    }
    if (this.#rmp.isDvr) {
      this.#setDvrVodUI();
    }
    this.#rmp.dom.current.style.width = (time * 100) + '%';
    this.#rmp.dom.handle.style.left = (time * 100) + '%';
    if (this.#rmp.googleCastConnected) {
      this.#rmp.seekTo(seekToTime * 1000).catch(error => {
        this.#rmp.rmpUtils.warning(1016, error);
      });
    } else {
      try {
        this.#rmp.dom.video.currentTime = seekToTime;
      } catch (warning) {
        console.warn(warning);
      }
    }
  }

}
